import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useCartVersion } from 'restful/tickets/queries/useCartVersion';
import {
  ticketsCartQuery,
  type TicketsCartQueryData,
} from 'restful/tickets/queries/useTicketsCart';
import { useTicketingCartId, useTicketingEventId } from 'restful/tickets/utils';
import { restCall } from 'services/api/restful/client';
import { RestApiError } from 'services/api/restful/errors';
import type { CartUpdateInput } from 'services/api/restful/generated/nofomoRestApi.schemas';
import { updateCart } from 'services/api/restful/generated/tickets/tickets';

type RemoveCartItemVariables = {
  itemIds: Array<string>;
};

export const useTicketsRemoveCartItem = () => {
  const { cartId, setCartId } = useTicketingCartId();
  const { eventId } = useTicketingEventId();
  const version = useCartVersion();
  const queryClient = useQueryClient();

  const { isPending, isPaused, mutate, error } = useMutation<
    TicketsCartQueryData,
    RestApiError,
    RemoveCartItemVariables,
    { previousValue?: TicketsCartQueryData }
  >({
    mutationFn: async ({ itemIds }) => {
      const input: CartUpdateInput = {
        version,
        action: { action: 'RemoveItems', itemIds },
      };

      const { data } = await restCall(() => updateCart(eventId, cartId, input));
      return { cart: data };
    },
    onMutate: async ({ itemIds }) => {
      const queryKey = ticketsCartQuery({ eventId, cartId }).queryKey;
      await queryClient.cancelQueries({ queryKey });
      const cart = queryClient.getQueryData<TicketsCartQueryData>(queryKey);
      if (cart?.cart) {
        const updatedCart = {
          ...cart.cart,
          items:
            cart.cart.items?.filter((item) => !itemIds.includes(item.id)) ?? [],
        };

        queryClient.setQueryData(queryKey, { cart: updatedCart });
      }
      return { previousValue: cart };
    },
    onError: (mutationError, _variables, context) => {
      if (mutationError.isNotFound) setCartId('');
      else if (context?.previousValue) {
        queryClient.setQueryData(
          ticketsCartQuery({ eventId, cartId }).queryKey,
          context.previousValue,
        );
      }
    },
    onSuccess: ({ cart }) => {
      if (cart?.id) {
        queryClient.setQueryData(
          ticketsCartQuery({ eventId, cartId: cart.id }).queryKey,
          { cart },
        );
        setCartId(cart.id);
      } else {
        queryClient.removeQueries({
          queryKey: ticketsCartQuery({ eventId, cartId }).queryKey,
        });
        setCartId('');
      }
    },
  });

  return {
    isLoading: isPending && !isPaused,
    removeCartItem: mutate,
    error,
  };
};
